import React, { useMemo, useCallback, useState, useEffect, memo } from "react";
import { router } from "@inertiajs/react";
import clsx from "clsx";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import {
  UnifiedCategoryService,
  CategoryTab,
  CategoryConfig,
} from "@/services/UnifiedCategoryService";
import { useUnifiedLocationFilter } from "@/hooks/useUnifiedLocationFilter";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface CarouselCategoryTabsProps {
  // Current active tab
  activeTab?: number | string;

  // Configuration overrides
  configOverrides?: Partial<CategoryConfig>;

  // Display options
  className?: string;
  showNewsCount?: boolean;
  showNavigationButtons?: boolean;
  autoSizeWidth?: boolean;
  tabsPerView?: number;

  // Callbacks
  onTabChange?: (categoryId: number | string, categorySlug?: string) => void;
  onConfigChange?: (config: CategoryConfig) => void;

  // Loading and error handling
  showLoadingState?: boolean;
  showErrorState?: boolean;
  maxRetries?: number;

  // Carousel specific options
  carouselOptions?: {
    align?: "start" | "center" | "end";
    loop?: boolean;
    skipSnaps?: boolean;
    containScroll?: "trimSnaps" | "keepSnaps" | false;
  };
}

/**
 * Enhanced Category Tabs with Shadcn Carousel
 * Provides smooth scrolling navigation for category tabs with better UX
 */
const CarouselCategoryTabs: React.FC<CarouselCategoryTabsProps> = ({
  activeTab,
  configOverrides = {},
  className = "",
  showNewsCount = false,
  showNavigationButtons = false,
  autoSizeWidth = true,
  tabsPerView = 5,
  onTabChange,
  onConfigChange,
  showLoadingState = true,
  showErrorState = true,
  maxRetries = 3,
  carouselOptions = {},
}) => {
  // State management
  const [tabs, setTabs] = useState<CategoryTab[]>([]);
  const [config, setConfig] = useState<CategoryConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [api, setApi] = useState<CarouselApi>();

  // Location filter integration
  const { currentLocationSlug } = useUnifiedLocationFilter();

  // Carousel configuration
  const carouselConfig = useMemo(
    () => ({
      align: "start" as const,
      loop: false,
      skipSnaps: false,
      containScroll: "trimSnaps" as const,
      ...carouselOptions,
      ...(tabs.length <= tabsPerView
        ? { dragFree: false }
        : { dragFree: true }),
    }),
    [carouselOptions, tabs.length, tabsPerView],
  );

  // Fetch categories and configuration
  const fetchCategoriesData = useCallback(async () => {
    if (retryCount >= maxRetries) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const [categoryTabs, categoryConfig] = await Promise.all([
        UnifiedCategoryService.getCategoryTabs(
          showNewsCount,
          currentLocationSlug || undefined,
          "Beranda",
        ),
        UnifiedCategoryService.getCategoryConfig(),
      ]);

      const finalConfig = { ...categoryConfig, ...configOverrides };
      const validatedConfig =
        UnifiedCategoryService.validateConfig(finalConfig);

      setTabs(categoryTabs);
      setConfig(validatedConfig);
      setRetryCount(0);

      if (onConfigChange) {
        onConfigChange(validatedConfig);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load categories";
      setError(errorMessage);
      console.error("Category tabs fetch error:", err);

      if (retryCount < maxRetries - 1) {
        const retryDelay = Math.pow(2, retryCount) * 1000;
        setTimeout(() => {
          setRetryCount((prev) => prev + 1);
        }, retryDelay);
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    showNewsCount,
    currentLocationSlug,
    retryCount,
    maxRetries,
    onConfigChange,
    configOverrides,
  ]);

  // Load categories on mount
  useEffect(() => {
    fetchCategoriesData();
  }, [showNewsCount, currentLocationSlug]);

  // Scroll to active tab when activeTab changes
  useEffect(() => {
    if (api && tabs.length > 0 && activeTab) {
      const activeIndex = tabs.findIndex(
        (tab) => tab.id === activeTab || tab.slug === activeTab,
      );
      if (activeIndex !== -1) {
        api.scrollTo(activeIndex);
      }
    }
  }, [api, tabs, activeTab]);

  // Determine active tab
  const resolvedActiveTab = useMemo(() => {
    if (activeTab !== undefined) return activeTab;
    if (config?.defaultCategory) return config.defaultCategory;
    return tabs[0]?.id || "home";
  }, [activeTab, config, tabs]);

  // Handle tab click
  const handleTabClick = useCallback(
    (tab: CategoryTab, event: React.MouseEvent) => {
      event.preventDefault();

      onTabChange?.(tab.id, tab.slug);

      const href = tab.slug ? `/category/${tab.slug}` : "/";
      const targetUrl = currentLocationSlug
        ? `${href}${href.includes("?") ? "&" : "?"}l=${currentLocationSlug}`
        : href;

      router.visit(targetUrl, {
        preserveState: false, // Changed to false to prevent state conflicts
        preserveScroll: true, // Changed to true to maintain scroll position
        replace: false,
      });
    },
    [onTabChange, currentLocationSlug],
  );

  // Handle manual refresh
  const handleRefresh = useCallback(() => {
    setRetryCount(0);
    fetchCategoriesData();
  }, [fetchCategoriesData]);

  // Render loading state
  if (isLoading && showLoadingState) {
    return (
      <div className={clsx("animate-pulse", className)}>
        <div className="flex space-x-4 py-3">
          {Array.from({ length: Math.min(tabsPerView, 6) }).map((_, index) => (
            <div
              key={`skeleton-${index}`}
              className="h-8 bg-gray-300 dark:bg-gray-600 rounded-full px-6 py-2 whitespace-nowrap min-w-[80px]"
            />
          ))}
        </div>
      </div>
    );
  }

  // Render error state
  if (error && showErrorState && retryCount >= maxRetries) {
    return (
      <div
        className={clsx(
          "flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800",
          className,
        )}
      >
        <div className="flex items-center space-x-3">
          <svg
            className="w-5 h-5 text-red-500"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-200">
              Failed to load categories
            </p>
            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          className="px-3 py-1 text-xs font-medium text-red-700 dark:text-red-200 bg-red-100 dark:bg-red-800 rounded hover:bg-red-200 dark:hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Don't render if no tabs available
  if (!tabs.length) {
    return null;
  }

  // Check if navigation is needed
  const needsNavigation = autoSizeWidth
    ? tabs.length > 5
    : tabs.length > tabsPerView;

  return (
    <div className={clsx("relative w-full", className)}>
      <Carousel opts={carouselConfig} setApi={setApi} className="w-full">
        <CarouselContent className="">
          {tabs.map((tab) => {
            const isActive =
              resolvedActiveTab === tab.id || resolvedActiveTab === tab.slug;

            return (
              <CarouselItem
                key={`tab-${tab.id}`}
                className={clsx(
                  "pl-2 md:pl-4",
                  autoSizeWidth ? "basis-auto" : "basis-auto",
                  // Responsive basis for different screen sizes (only when autoSizeWidth is false)
                  !autoSizeWidth && {
                    "basis-1/2 sm:basis-1/3 md:basis-1/4 lg:basis-1/5 xl:basis-1/6":
                      tabsPerView >= 6,
                    "basis-1/2 sm:basis-1/3 md:basis-1/4 lg:basis-1/5":
                      tabsPerView === 5,
                    "basis-1/2 sm:basis-1/3 md:basis-1/4": tabsPerView === 4,
                    "basis-1/2 sm:basis-1/3": tabsPerView === 3,
                    "basis-1/2": tabsPerView === 2,
                    "basis-full": tabsPerView === 1,
                  },
                )}
              >
                <button
                  onClick={(e) => handleTabClick(tab, e)}
                  className={clsx(
                    autoSizeWidth
                      ? "px-3 py-1.5 md:px-4 md:py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap flex items-center justify-center space-x-2 min-w-fit"
                      : "w-full px-3 py-1.5 md:px-4 md:py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap flex items-center justify-center space-x-2 min-w-0",
                    {
                      // Active state
                      "bg-red-600 text-white shadow-lg transform scale-105 border-2 border-red-600 cursor-pointer":
                        isActive,
                      // Inactive state
                      "border-2 font-semibold bg-black/10 dark:bg-black/20 border-red-700 text-red-500 hover:text-white hover:bg-red-600 hover:shadow-md hover:scale-102 cursor-pointer":
                        !isActive,
                    },
                  )}
                  title={`View ${tab.label} news${
                    tab.newsCount ? ` (${tab.newsCount} articles)` : ""
                  }`}
                >
                  {/* Tab icon if available */}
                  {tab.icon && (
                    <span className="text-current flex-shrink-0">
                      {tab.icon}
                    </span>
                  )}

                  {/* Tab label */}
                  <span className="truncate">{tab.label}</span>

                  {/* News count if enabled and available */}
                  {showNewsCount && tab.newsCount !== undefined && (
                    <span
                      className={clsx(
                        "text-xs px-1.5 py-0.5 rounded-full flex-shrink-0",
                        isActive
                          ? "bg-white/20 text-white"
                          : "bg-red-100 text-red-600",
                      )}
                    >
                      {tab.newsCount}
                    </span>
                  )}
                </button>
              </CarouselItem>
            );
          })}
        </CarouselContent>

        {/* Navigation buttons - only show if needed and enabled */}
        {needsNavigation && showNavigationButtons && (
          <>
            <CarouselPrevious
              className={clsx(
                "left-0 h-8 w-8 bg-white/90 dark:bg-gray-800/90 border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-800 shadow-md",
                "hidden sm:flex", // Hide on mobile, show on sm and up
              )}
            >
              <ChevronLeft className="h-4 w-4" />
            </CarouselPrevious>
            <CarouselNext
              className={clsx(
                "right-0 h-8 w-8 bg-white/90 dark:bg-gray-800/90 border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-800 shadow-md",
                "hidden sm:flex", // Hide on mobile, show on sm and up
              )}
            >
              <ChevronRight className="h-4 w-4" />
            </CarouselNext>
          </>
        )}
      </Carousel>

      {/* Show indicator if there are more tabs than visible on mobile */}
      {needsNavigation && !autoSizeWidth && (
        <div className="flex justify-center mt-2 sm:hidden">
          <div className="flex space-x-1">
            {Array.from({ length: Math.ceil(tabs.length / tabsPerView) }).map(
              (_, index) => (
                <div
                  key={index}
                  className="h-1.5 w-1.5 rounded-full bg-gray-300 dark:bg-gray-600"
                />
              ),
            )}
          </div>
        </div>
      )}

      {/* Loading indicator for news count updates */}
      {isLoading && !showLoadingState && (
        <div className="absolute top-2 right-2">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-red-600"></div>
        </div>
      )}
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default memo(CarouselCategoryTabs, (prevProps, nextProps) => {
  // Custom comparison for better performance
  return (
    prevProps.activeTab === nextProps.activeTab &&
    prevProps.showNavigationButtons === nextProps.showNavigationButtons &&
    prevProps.autoSizeWidth === nextProps.autoSizeWidth &&
    prevProps.showNewsCount === nextProps.showNewsCount &&
    prevProps.className === nextProps.className &&
    JSON.stringify(prevProps.configOverrides) === JSON.stringify(nextProps.configOverrides)
  );
});

// Export hook for external use
export const useCarouselCategoryTabs = (locationSlug?: string) => {
  const [tabs, setTabs] = useState<CategoryTab[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    UnifiedCategoryService.getCategoryTabs(false, locationSlug, "Beranda")
      .then((fetchedTabs) => {
        if (isMounted) {
          setTabs(fetchedTabs);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching category tabs:", error);
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [locationSlug]);

  return { tabs, isLoading };
};
